"use client";

import { Button } from "@/components/ui/button";
import MapIcon from "@heroicons/react/24/solid/MapIcon";
import PhoneIcon from "@heroicons/react/24/solid/PhoneIcon";
import { VENUE_TABS } from "@/@core/constants/venue-tabs";
import WhatsappButton from "@/components/WhatsappButton";
import {
  WHATSAPP_SECTIONS,
  WHATSAPP_TAGS,
} from "@/@core/enums/whatsapp_tags.enum";
import { useMemo, useState } from "react";
import getTopDiscountByVenue from "@/lib/get-top-discount-by-venue";
import WhatsappIcon from "@/components/hero-icons/whatsapp-icon";
import dynamic from "next/dynamic";
import dynamicallyCreateAnchorAndNavigate from "@/lib/navigate-url";
import { useUserLocation } from "@/context/UserLocationContext";
import { getRouteDistance } from "@/services/osrm.service";

const VenueDetailShareModal = dynamic(
  () => import("../new-details/share/Modal"),
);

const MapComponent = dynamic(() => import('./Contact/MapComponent'), {
  ssr: false,
  loading: () => (
    <div className="relative w-[300px] h-[150px] flex items-center justify-center bg-gray-100 rounded-lg">
      <div className="flex items-center justify-center space-x-1">
        <div className="w-2 h-2 bg-black rounded-full animate-bounce"></div>
        <div className="w-2 h-2 bg-black rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
        <div className="w-2 h-2 bg-black rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
      </div>
    </div>
  )
});

interface Props {
  datas: {
    address: string;
    googleAdressLink: string;
    clickAdressLink: string;
  };
  venue: Venue.Detail;
  whatsappPhone: string;
  id: string | number;
}

export default function Contact(props: Props) {
  const [isOpen, setOpen] = useState(false);

  function getLatLng(url) {
    const latMatch = url.match(/3d(-?\d+\.\d+)/);
    const lngMatch = url.match(/2d(-?\d+\.\d+)/);
    const zoomMatch = url.match(/4f(\d+\.\d+)/);

    const latitude = latMatch ? latMatch[1] : null;
    const longitude = lngMatch ? lngMatch[1] : null;
    const zoom = zoomMatch ? zoomMatch[1] : null;

    return { latitude, longitude, zoom };
  }

  const parsedLocation = useMemo(() => {
    return getLatLng(props.datas.googleAdressLink);
  }, [props.datas.googleAdressLink]);

  const mapCenter = useMemo(() => {
    return {
      latitude: parseFloat(parsedLocation.latitude || "0"),
      longitude: parseFloat(parsedLocation.longitude || "0"),
    };
  }, [parsedLocation.latitude, parsedLocation.longitude]);

  const mapLink = useMemo(() => {
    const lat = parseFloat(parsedLocation.latitude || "0");
    const lng = parseFloat(parsedLocation.longitude || "0");

    return `https://www.google.com/maps/place/${lat},${lng}`;
  }, [parsedLocation.latitude, parsedLocation.longitude]);

  const { userLocation } = useUserLocation();

  const distanceLabel = useMemo(() => {
    if (!userLocation) return null;
    if (!mapCenter.latitude || !mapCenter.longitude) return null;

    const result = getRouteDistance(
      userLocation.lat,
      userLocation.lng,
      mapCenter.latitude,
      mapCenter.longitude,
    );
    if (!result) return null;

    const formatted = result.distance.toFixed(1).replace(".", ",");
    return `Size ${formatted} km uzakta`;
  }, [userLocation, mapCenter.latitude, mapCenter.longitude]);


  return (
    <div
      id={VENUE_TABS.CONTACT}
      className="flex flex-col border border-gray p-4 rounded-[32px] gap-4 lg:max-w-[863px] mt-6"
    >
      {isOpen && (
        <VenueDetailShareModal
          isShareOpen={isOpen}
          onClose={() => setOpen(false)}
        />
      )}
      <div className="flex bg-stone-50 rounded-[16px] py-1.5 px-2">
        <div className="flex items-center gap-2 ">
          <PhoneIcon className="size-5 text-primary" />
          <h2 className="font-bold text-sm">
            İletişim
          </h2>
        </div>
      </div>

      <div className="flex md:flex-row flex-col md:justify-normal justify-center gap-4">
        <div>
          <MapComponent
            latitude={mapCenter.latitude}
            longitude={mapCenter.longitude}
            zoom={parsedLocation.zoom || "13"}
            mapLink={mapLink}
          />
        </div>
        <div className="flex flex-col md:w-1/2 gap-4">
          <span className="font-medium text-sm text-black">
            {props.datas.address}
          </span>
          {distanceLabel && (
            <span className="font-medium text-sm text-secondary">
              {distanceLabel}
            </span>
          )}

          <div className="flex max-md:flex-col flex-wrap gap-2 items-center justify-center md:justify-normal">
            <Button
              variant="assistant"
              className="py-[9px] px-3 text-sm flex gap-2 rounded-full"
              onClick={() =>
                dynamicallyCreateAnchorAndNavigate(props.datas.clickAdressLink)
              }
            >
              <MapIcon className="size-4" /> Yol Tarifi Al
            </Button>

            <div className="flex gap-2 items-center justify-center">
              <Button
                variant="assistant"
                onClick={() => setOpen(true)}
                className="py-[9px] px-3 text-sm flex gap-2 rounded-full"
              >
                <PhoneIcon className="size-4" /> Ara
              </Button>

              <WhatsappButton
                section={WHATSAPP_SECTIONS.VENUE_DETAIL}
                translateValues={{
                  "discount-visitors": getTopDiscountByVenue(
                    props.venue as Venue.Detail,
                  ),
                }}
                tag={WHATSAPP_TAGS.NORMAL}
                className="bg-white text-[#25D366] border-[#25D366] rounded-full
                        hover:text-[#25D366] hover:bg-[#25D366] hover:bg-[#25D366]/20 py-[9px] px-3"
                variant="default"
                phoneNumber={props?.whatsappPhone}
                venue={props.venue}
              >
                <WhatsappIcon className="size-4" /> Whatsapp
              </WhatsappButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
